import { FailureTypeRow } from '../failure-types-table';

interface SagaInvocationLike {
  id: string;
  orgId: string | null;
  sagaId: string | null;
  sagaType: string | null;
  entityName: string | null;
  completionResult: string | null;
  completionFailure: string | null;
}

/**
 * Groups failed saga invocations by their (truncated) failure message into
 * {@link FailureTypeRow}s, including a per-org count breakdown so the UI can
 * show which orgs are impacted by each error.
 */
export function buildSagaFailureRows(
  invocations: SagaInvocationLike[]
): FailureTypeRow[] {
  const failureGroups = new Map<
    string,
    {
      count: number;
      example: SagaInvocationLike;
      orgCounts: Map<string, number>;
    }
  >();

  for (const inv of invocations) {
    if (inv.completionResult !== 'failure' || inv.completionFailure === null) {
      continue;
    }
    const key = inv.completionFailure.substring(0, 200);
    const orgKey = inv.orgId ?? 'unknown';
    const existing = failureGroups.get(key);
    if (existing === undefined) {
      failureGroups.set(key, {
        count: 1,
        example: inv,
        orgCounts: new Map([[orgKey, 1]]),
      });
    } else {
      existing.count++;
      existing.orgCounts.set(orgKey, (existing.orgCounts.get(orgKey) ?? 0) + 1);
    }
  }

  const rows: FailureTypeRow[] = [];
  for (const [failureType, { count, example, orgCounts }] of failureGroups) {
    rows.push({
      failureType,
      count,
      exampleOrgId: `saga-${example.orgId ?? 'unknown'}-${
        example.sagaId ?? 'unknown'
      }`,
      exampleRunId: '',
      exampleId: example.id,
      sagaMetadata: {
        sagaType: example.sagaType,
        entityName: example.entityName,
      },
      orgBreakdown: [...orgCounts.entries()]
        .map(([orgId, c]) => ({ orgId, count: c }))
        .sort((a, b) => b.count - a.count),
    });
  }
  rows.sort((a, b) => b.count - a.count);
  return rows;
}
